import React from 'react';
import { LANDING_STATS } from './statsData';

const styles = `
  /* Variables and font imports are handled by global.css */

  .hero {
    min-height: 100vh;
    min-height: 100svh; /* mobile-safe viewport height */
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 96px 60px 72px;   /* extra top/bottom for breathing room */
    position: relative;
    overflow: hidden;
    background: #0a0900;
    color: #fefce8;
    font-family: 'DM Sans', sans-serif;
    font-weight: 300;
  }

  .hero-glow {
    position: absolute; inset: 0; pointer-events: none;
    background:
      radial-gradient(ellipse 70% 55% at 72% 38%, rgba(245,200,0,0.10) 0%, transparent 60%),
      radial-gradient(ellipse 45% 45% at 18% 78%, rgba(230,168,0,0.07) 0%, transparent 52%),
      radial-gradient(ellipse 30% 30% at 50% 10%, rgba(245,200,0,0.04) 0%, transparent 50%);
  }

  .hero-grid {
    position: absolute; inset: 0; pointer-events: none;
    background-image:
      linear-gradient(rgba(245,200,0,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(245,200,0,0.025) 1px, transparent 1px);
    background-size: 72px 72px;
    mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%);
    -webkit-mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%);
  }

  .hero-vignette {
    position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(ellipse 100% 100% at 50% 0%, transparent 50%, rgba(10,9,0,0.6) 100%);
  }

  .hero-badge {
    position: absolute;
    top: 92px; right: 60px;   /* 80px hero padding + 12px breathing room */
    display: flex; align-items: center; gap: 8px;
    background: rgba(245,200,0,0.08);
    border: 1px solid rgba(245,200,0,0.2);
    border-radius: 100px;
    padding: 8px 16px;
    font-size: 12px; letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--accent);
    animation: fadeDown 0.8s ease 0.4s both;
    z-index: 3;
    will-change: opacity, transform;
  }

  .badge-dot {
    width: 7px; height: 7px; border-radius: 50%; background: #22c55e;
    animation: pulseDot 2s ease-in-out infinite;
    flex-shrink: 0;
  }

  .counter-strip {
    position: absolute;
    top: 50%; right: 60px;
    transform: translateY(-50%);
    display: flex; flex-direction: column; gap: 32px;
    animation: fadeLeft 0.9s ease 0.6s both;
    z-index: 3;
    will-change: opacity, transform;
  }

  .counter-item { text-align: right; }

  .counter-num {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 48px; line-height: 1;
    color: var(--white);
  }

  .counter-accent { color: var(--accent); }

  .counter-label {
    font-size: 10px; letter-spacing: 2.5px; text-transform: uppercase;
    color: var(--muted); margin-top: 3px;
  }

  .counter-divider {
    width: 28px; height: 1px; background: var(--border);
    margin-left: auto; margin-top: 8px;
  }

  .hero-eyebrow {
    display: flex; align-items: center; gap: 14px;
    font-size: 11px; letter-spacing: 4px; text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 24px; margin-top: 48px;
    position: relative; z-index: 2;
    animation: fadeUp 0.7s ease 0.1s both;
  }

  .eyebrow-line { width: 36px; height: 1px; background: var(--accent); flex-shrink: 0; }
  .eyebrow-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--accent); margin-left: 4px; flex-shrink: 0; }

  .hero-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(44px, 9vw, 132px);
    line-height: 0.95;
    letter-spacing: 2px;
    color: var(--white);
    position: relative; z-index: 2;
    animation: fadeUp 0.8s ease 0.2s both;
    will-change: opacity, transform;
  }

  .title-italic {
    font-family: Georgia, 'Times New Roman', serif;
    font-style: italic;
    color: var(--gold);
    display: block;
    letter-spacing: 1px;
  }

  .title-outline {
    -webkit-text-stroke: 1.5px rgba(245,200,0,0.35);
    color: transparent;
  }

  .hero-bottom {
    display: flex; align-items: flex-end; justify-content: space-between;
    margin-top: 40px; margin-bottom: 80px;
    position: relative; z-index: 2;
    gap: 32px; flex-wrap: wrap;
    animation: fadeUp 0.9s ease 0.35s both;
  }

  .hero-desc {
    max-width: 380px; font-size: 15px; line-height: 1.85; color: var(--muted);
  }

  .hero-desc strong { color: var(--white); font-weight: 500; }

  .hero-tags {
    display: flex; gap: 8px; flex-wrap: wrap; margin-top: 16px;
  }

  .tag {
    font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase;
    padding: 5px 12px; border-radius: 100px;
    border: 1px solid var(--border-soft); color: var(--muted);
    transition: border-color 0.2s, color 0.2s; cursor: default;
  }

  .tag:hover { border-color: rgba(245,200,0,0.3); color: var(--accent); }

  .hero-ctas {
    display: flex; flex-direction: column; align-items: flex-end; gap: 16px;
  }

  .btn-primary {
    display: inline-flex; align-items: center; gap: 10px;
    background: var(--accent); color: #0a0900;
    padding: 16px 44px; font-size: 12px; letter-spacing: 2.5px; text-transform: uppercase;
    text-decoration: none; border-radius: 2px; font-family: 'DM Sans', sans-serif;
    font-weight: 500; transition: background 0.25s, transform 0.2s;
    position: relative; overflow: hidden;
  }

  .btn-primary:hover { background: #e6a800; transform: translateY(-2px); }

  .btn-ghost {
    display: inline-flex; align-items: center; gap: 10px;
    color: var(--muted); font-size: 12px; letter-spacing: 2px; text-transform: uppercase;
    text-decoration: none; transition: color 0.25s, gap 0.25s;
  }

  .btn-ghost:hover { color: var(--white); gap: 16px; }

  .scroll-cue {
    position: absolute; bottom: 120px; left: 60px;
    display: flex; align-items: center; gap: 10px;
    font-size: 10px; letter-spacing: 2px; text-transform: uppercase; color: var(--muted);
    opacity: 0.6; z-index: 2;
    animation: fadeUp 1s ease 1s both;
  }

  .scroll-line {
    width: 1px; height: 32px;
    background: linear-gradient(to bottom, transparent, var(--accent));
    animation: growLine 2s ease-in-out infinite;
  }

  .accent-circle {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(245,200,0,0.08);
    pointer-events: none;
  }

  .deco-svg {
    position: absolute;
    top: 80px; right: 220px;
    opacity: 0.12;
    animation: spinSlow 40s linear infinite;
    pointer-events: none;
    will-change: transform;
    contain: strict;
  }

  .stats-bar {
    display: flex; gap: 0;
    position: absolute; bottom: 0; left: 0; right: 0;
    border-top: 1px solid var(--border-soft);
    background: rgba(10,9,0,0.7);
    backdrop-filter: blur(12px);
    animation: fadeUp 0.9s ease 0.8s both;
    z-index: 4;
    will-change: opacity, transform;
  }

  .stat-item {
    flex: 1; padding: 18px 28px;
    border-right: 1px solid var(--border-soft);
    display: flex; align-items: center; gap: 14px;
  }

  .stat-item:last-child { border-right: none; }

  .stat-icon {
    width: 36px; height: 36px; border-radius: 6px;
    background: rgba(245,200,0,0.1);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  .stat-icon svg {
    width: 16px; height: 16px; stroke: var(--accent);
    fill: none; stroke-width: 1.5;
    stroke-linecap: round; stroke-linejoin: round;
  }

  .stat-num {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 26px; color: var(--white); line-height: 1;
  }

  .stat-label {
    font-size: 10px; letter-spacing: 1.5px;
    text-transform: uppercase; color: var(--muted); margin-top: 2px;
  }

  /* Keyframes */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeDown {
    from { opacity: 0; transform: translateY(-16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeLeft {
    from { opacity: 0; transform: translate(20px, -50%); }
    to   { opacity: 1; transform: translate(0, -50%); }
  }
  @keyframes pulseDot {
    0%,100% { opacity: 1; transform: scale(1); }
    50%     { opacity: 0.6; transform: scale(1.3); }
  }
  @keyframes growLine {
    0%,100% { transform: scaleY(1); opacity: 0.6; }
    50%     { transform: scaleY(1.4); opacity: 1; }
  }
  @keyframes floatUp {
    0%,100% { transform: translateY(0); }
    50%     { transform: translateY(-12px); }
  }
  @keyframes spinSlow {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }

  /* Responsive */
  @media (max-width: 900px) {
    .hero          { padding: 86px 24px 72px; }
    .hero-badge    { top: 84px; right: 24px; font-size: 11px; padding: 7px 14px; }
    .counter-strip { display: none; }
    .scroll-cue    { left: 24px; bottom: 110px; }
    .hero-ctas     { align-items: flex-start; }
    .hero-desc     { max-width: 100%; }
    .stat-item     { padding: 14px 16px; }
    .deco-svg      { display: none; }
  }

  @media (max-width: 640px) {
    .hero          { padding: 82px 16px 72px; }
    .hero-badge    {
      position: static;
      align-self: flex-start;
      margin: 0 0 18px;
      font-size: 10px;
      padding: 6px 10px;
      letter-spacing: 1px;
      max-width: 100%;
      z-index: 2;
    }
    .hero-eyebrow  { margin-top: 12px; font-size: 9px; letter-spacing: 3px; }
    .hero-title    { font-size: clamp(36px, 10vw, 54px); }
    .hero-bottom   { flex-direction: column; align-items: flex-start; margin-top: 32px; margin-bottom: 72px; gap: 24px; }
    .hero-ctas     { width: 100%; align-items: flex-start; }
    .btn-primary   { width: 100%; justify-content: center; padding: 14px 20px; }
    .btn-ghost     { font-size: 11px; }
    .stats-bar     { flex-wrap: wrap; position: static; margin-top: 8px; border-radius: 8px; overflow: hidden; }
    .stat-item     { flex: 1 1 50%; padding: 14px 16px; }
    .scroll-cue    { display: none; }
  }

  @media (max-width: 400px) {
    .hero          { padding: 78px 14px 72px; }
    .hero-badge    { margin-bottom: 16px; }
    .hero-title    { font-size: clamp(32px, 9vw, 46px); }
    .hero-desc     { font-size: 14px; }
    .stat-item     { flex: 1 1 100%; }
  }
`;

/* ── Small reusable SVGs ── */
const ArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const StatIcon = ({ d }) => (
  <svg viewBox="0 0 24 24">
    <path d={d} />
  </svg>
);

const COUNTERS = [
  { num: LANDING_STATS.projectsCompleted.toString(), suffix: '+', label: 'Projects Completed' },
  { num: LANDING_STATS.clientSatisfaction.toString(), suffix: '%', label: 'Client Satisfaction' },
  { num: LANDING_STATS.yearsExperience.toString(), suffix: '+', label: 'Years Experience' },
];

const TAGS = ['Performance', 'Conversion', 'Growth', 'Results'];

const STATS = [
  { num: LANDING_STATS.projectsCompleted + '+', label: 'Websites Launched', icon: 'M2 3h20v14H2zM8 21h8M12 17v4' },
  { num: '3×', label: 'Avg Lead Growth', icon: 'M22 12h-4l-3 9L9 3l-3 9H2' },
  { num: 'top 10', label: 'SEO Rankings', icon: 'M11 11m-8 0a8 8 0 1 0 16 0 8 8 0 0 0-16 0M21 21l-4.35-4.35' },
  { num: LANDING_STATS.happyClients + '+', label: 'Happy Clients', icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75' },
];

/* ── Component ── */
const LandingHero = () => (
  <>
    <style>{styles}</style>

    <section className="hero">

      {/* Backgrounds */}
      <div className="hero-glow" />
      <div className="hero-grid" />
      <div className="hero-vignette" />

      {/* Decorative floating circles */}
      <div className="accent-circle" style={{
        width: 520, height: 520, top: -120, right: -100,
        opacity: 0.4, animation: 'floatUp 8s ease-in-out infinite',
      }} />
      <div className="accent-circle" style={{
        width: 280, height: 280, top: 60, right: 100,
        opacity: 0.25, animation: 'floatUp 10s ease-in-out infinite 2s',
      }} />
      <div className="accent-circle" style={{
        width: 120, height: 120, bottom: 200, left: 200,
        opacity: 0.2, animation: 'floatUp 7s ease-in-out infinite 1s',
        borderColor: 'rgba(245,200,0,0.15)',
      }} />

      {/* Spinning geometric SVG */}
      <svg className="deco-svg" width="80" height="80" viewBox="0 0 80 80" fill="none">
        <circle cx="40" cy="40" r="38" stroke="#f5c800" strokeWidth="0.5" strokeDasharray="4 4" />
        <circle cx="40" cy="40" r="20" stroke="#f5c800" strokeWidth="0.5" />
        <line x1="40" y1="2" x2="40" y2="78" stroke="#f5c800" strokeWidth="0.5" />
        <line x1="2" y1="40" x2="78" y2="40" stroke="#f5c800" strokeWidth="0.5" />
      </svg>

      {/* Live badge */}
      <div className="hero-badge">
        <span className="badge-dot" />
        Available for new projects
      </div>

      {/* Right-side counter */}
      <div className="counter-strip">
        {COUNTERS.map((c, i) => (
          <div className="counter-item" key={i}>
            <div className="counter-num">
              <span className="counter-accent">{c.num}</span>{c.suffix}
            </div>
            <div className="counter-label">{c.label}</div>
            {i < COUNTERS.length - 1 && <div className="counter-divider" />}
          </div>
        ))}
      </div>

      {/* Eyebrow */}
      <div className="hero-eyebrow">
        <span className="eyebrow-line" />
        Digital Marketing Agency
        <span className="eyebrow-dot" />
      </div>

      {/* Headline */}
      <h1 className="hero-title">
        We Build<br />
        <span className="title-outline">Brands</span> That<br />
        <span className="title-italic">Dominate.</span>
      </h1>

      {/* Bottom row */}
      <div className="hero-bottom">
        <div>
          <p className="hero-desc">
            From <strong>strategy to execution</strong> — we craft digital experiences
            that convert visitors into loyal customers and drive{' '}
            <strong>measurable growth</strong> across every channel.
          </p>
          <div className="hero-tags">
            {TAGS.map(tag => (
              <span className="tag" key={tag}>{tag}</span>
            ))}
          </div>
        </div>

        <div className="hero-ctas">
          <a href="#services" className="btn-primary">
            Explore Services <ArrowRight />
          </a>
          <a href="#contact" className="btn-ghost">
            Start a Project <ArrowRight />
          </a>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="scroll-cue">
        <div className="scroll-line" />
        Scroll
      </div>

      {/* Stats bar */}
      <div className="stats-bar">
        {STATS.map(s => (
          <div className="stat-item" key={s.label}>
            <div className="stat-icon">
              <StatIcon d={s.icon} />
            </div>
            <div>
              <div className="stat-num">{s.num}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

    </section>
  </>
);

export default LandingHero;